import {
  BrandKitDiff,
  BrandKitField,
  BrandKitFieldAttribute,
  BrandKitFieldChangeStatus,
  BrandKitFieldDiff,
  BrandKitFieldType,
  BrandKitListItemType,
} from "@arrowsup/platform-dtos";
import { Alert, Box, Chip, Stack, Typography } from "@mui/material";
import { useContext } from "react";
import { BrandAssetsContext } from "./brand-assets";
import { BrandKitFieldValue, describeFieldType } from "./brand-kit-field-value";

const statusColors = {
  [BrandKitFieldChangeStatus.Added]: "success",
  [BrandKitFieldChangeStatus.Removed]: "error",
  [BrandKitFieldChangeStatus.Changed]: "info",
} as const;

const attributeLabels: Record<BrandKitFieldAttribute, string> = {
  [BrandKitFieldAttribute.Label]: "label",
  [BrandKitFieldAttribute.Description]: "description",
  [BrandKitFieldAttribute.Type]: "type",
  [BrandKitFieldAttribute.Value]: "value",
};

const statusLabel = (diff: BrandKitFieldDiff): string => {
  switch (diff.status) {
    case BrandKitFieldChangeStatus.Added:
      return "Added";
    case BrandKitFieldChangeStatus.Removed:
      return "Removed";
    case BrandKitFieldChangeStatus.Changed: {
      const changed = diff.changed.map((a) => attributeLabels[a]).join(", ");

      return `Changed ${changed}`;
    }
    // no default
  }
};

const Side: React.FC<{
  title: string;
  field: BrandKitField;
  showLabel: boolean;
  showMeta: boolean;
}> = ({ title, field, showLabel, showMeta }) => (
  <Box flex={1} minWidth={200}>
    <Typography variant="overline" color="text.secondary">
      {title}
    </Typography>
    <Stack spacing={0.5}>
      {showLabel && (
        <Typography variant="body2">Label: {field.label}</Typography>
      )}
      {showMeta && (
        <Typography variant="caption" color="text.secondary">
          {describeFieldType(field)}
          {field.description ? ` · ${field.description}` : ""}
        </Typography>
      )}
      <BrandKitFieldValue field={field} />
    </Stack>
  </Box>
);

const FieldDiffRow: React.FC<{ diff: BrandKitFieldDiff }> = ({ diff }) => {
  const assets = useContext(BrandAssetsContext);
  const field = diff.after ?? diff.before;

  // Image lists hold asset ids; name the images instead. Each side has its
  // own item type, since a list's type may have changed.
  const namer = (side?: BrandKitField) => {
    const images =
      side?.type === BrandKitFieldType.List &&
      side.itemType === BrandKitListItemType.Image;

    return (item: string) =>
      images ? assets.get(item)?.name ?? "an unknown image" : item;
  };

  const addedName = namer(diff.after);
  const removedName = namer(diff.before);
  const showLabel = diff.changed.includes(BrandKitFieldAttribute.Label);

  const showMeta =
    diff.changed.includes(BrandKitFieldAttribute.Type) ||
    diff.changed.includes(BrandKitFieldAttribute.Description);

  const listItems = diff.listItems
    ? [
        ...diff.listItems.added.map((item) => `+ ${addedName(item)}`),
        ...diff.listItems.removed.map((item) => `− ${removedName(item)}`),
      ]
    : [];

  return (
    <Box sx={{ border: 1, borderColor: "divider", borderRadius: 1, p: 2 }}>
      <Stack direction="row" spacing={1} alignItems="center" mb={1}>
        <Typography variant="subtitle1">{field?.label}</Typography>
        <Chip
          size="small"
          color={statusColors[diff.status]}
          label={statusLabel(diff)}
        />
      </Stack>
      {listItems.length > 0 && (
        <Typography variant="body2" color="text.secondary" mb={1}>
          {listItems.join("   ")}
        </Typography>
      )}
      <Box display="flex" gap={3} flexWrap="wrap">
        {diff.before && (
          <Side
            title={diff.after ? "Before" : "Removed"}
            field={diff.before}
            showLabel={showLabel}
            showMeta={showMeta || !diff.after}
          />
        )}
        {diff.after && (
          <Side
            title={diff.before ? "After" : "Added"}
            field={diff.after}
            showLabel={showLabel}
            showMeta={showMeta || !diff.before}
          />
        )}
      </Box>
    </Box>
  );
};

export const BrandKitDiffView: React.FC<{ diff: BrandKitDiff }> = ({
  diff,
}) => {
  if (diff.fields.length === 0 && !diff.reordered) {
    return <Typography color="text.secondary">No changes.</Typography>;
  }

  return (
    <Stack spacing={2}>
      {diff.reordered && <Alert severity="info">Fields were reordered.</Alert>}
      {diff.fields.map((d) => (
        <FieldDiffRow key={`${d.status}-${d.key}`} diff={d} />
      ))}
    </Stack>
  );
};
